package dev.lingvo.site.pages.app.settings.tokens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.CreateTokenRequest
import dev.lingvo.shared.CreatedTokenDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.TokenScope
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LocalToasts
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.enterRise
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Text

/**
 * Minting a token, on a route rather than in a modal.
 *
 * A token grant is a decision with a URL somebody should be able to send to their security team, and
 * a modal has no URL. That is the whole reason this is a page.
 *
 * Scopes name **the consequence, not the verb**: what `files:write` is called matters less than the
 * fact that it can remove work. And a live summary restates the whole grant in English before the
 * button, because a row of checkboxes is not something anybody reads back to themselves.
 */
@Page("/app/settings/tokens/new")
@Layout(".components.layouts.AppLayout")
@Composable
fun NewTokenPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    // The shell's channel, not a host of this page's own: this card navigates away on Done, and a
    // page that toasts and then unmounts its own host destroys the message before it can be read.
    val toasts = LocalToasts.current
    // Explicitly typed. `toasts::show` has three parameters and one default-carrying overload; an
    // inferred callable reference here bound to a shape the extractor's `notify(` site could not
    // recognise, so every toast on this page stayed English.
    val notify: (String, LToastVariant, Long) -> Int = toasts::show
    val role = Session.strongestRole()

    var name by remember { mutableStateOf("") }
    var scopes by remember { mutableStateOf(TokenScope.DEFAULT) }
    var expiry by remember { mutableStateOf("90") }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var created by remember { mutableStateOf<CreatedTokenDto?>(null) }
    var copyFailure by remember { mutableStateOf<String?>(null) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.settings.tokens.new.new_api_token", "New API token"),
            t("app.settings.tokens.new.for_ci_and_scripts_to_connect_2", "For CI and scripts. To connect a terminal, run `lingvo login --device` instead — " +
                "nothing gets copied and nothing lands in your shell history."),
        )

        created?.let { token ->
            // Arrives in response to a click, replacing the form that was there. The rise says the
            // card is the answer to what was just done rather than an unrelated page.
            LCard(Modifier.fillMaxWidth().enterRise()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LAlert(
                        t("app.settings.tokens.new.copy_this_now", "Copy this now"),
                        t("app.settings.tokens.new.it_is_shown_once_lingvo_stores", "It is shown once. Lingvo stores only a hash and cannot show it again."),
                        variant = LAlertVariant.WARNING,
                    )
                    // A bare `<pre>` pushed the tail of a 36-character token off the card on a
                    // phone, with no scroller and no copy button — and Lingvo keeps only a hash, so
                    // the value is gone the moment this card is.
                    LOneTimeSecret(
                        value = token.token,
                        label = tr("app.settings.tokens.new.new_api_token", "New API token"),
                        copyLabel = t("app.settings.tokens.new.copy_token", "Copy token"),
                        onCopied = {
                            copyFailure = null
                            notify(tr("app.settings.tokens.new.token_copied", "Token copied"), LToastVariant.SUCCESS, 4_000)
                        },
                        // Inline, and it names the object and the deadline — the agent cards'
                        // sentence. A toast that clears itself after six seconds is the wrong
                        // carrier for "select this and copy it by hand".
                        onCopyFailed = {
                            copyFailure = tr(
                                "app.settings.tokens.new.couldnt_reach_the_clipboard_select_the",
                                "Couldn't reach the clipboard. Select the token above and copy it " +
                                    "manually before leaving.",
                            )
                        },
                        trailingAction = {
                            LButton(
                                t("app.settings.tokens.new.done", "Done"),
                                { ctx.router.tryRoutingTo("/app/settings/tokens") },
                                variant = LButtonVariant.SECONDARY,
                            )
                        },
                    )
                    // Reached only by pressing copy and having it refused, so it is announced.
                    copyFailure?.let {
                        LAlert(
                            t("app.settings.tokens.new.copy_it_by_hand", "Copy it by hand"),
                            it,
                            variant = LAlertVariant.WARNING,
                            live = true,
                        )
                    }
                }
            }
            return@Column
        }

        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.LG)) {
                LTextField(
                    name,
                    { name = it },
                    Modifier.fillMaxWidth(),
                    label = t("app.settings.tokens.new.what_is_it_for", "What is it for?"),
                    placeholder = t("app.settings.tokens.new.release_pipeline", "Release pipeline"),
                    enabled = !busy,
                )

                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.tokens.new.what_it_may_do", "What it may do"))
                    TokenScope.entries.forEach { option ->
                        // Above the caller's role: drawn, disabled, and given the reason. Hiding it
                        // would mean a translator never learns the surface exists, and then cannot
                        // ask for it.
                        val allowed = role?.atLeast(option.minimumRole) == true
                        Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
                            LCheckbox(
                                checked = option in scopes,
                                onCheckedChange = { checked ->
                                    scopes = if (checked) scopes + option else scopes - option
                                },
                                label = option.id,
                                enabled = allowed && !busy,
                            )
                            LMuted(option.consequence)
                            if (!allowed) {
                                LMuted(
                                    tf(
                                        "app.settings.tokens.new.needs_role_or_above",
                                        "Needs {role} or above. A token can never do more than " +
                                            "the person who made it.",
                                        "role" to option.minimumRole.name.lowercase(),
                                    ),
                                )
                            }
                        }
                    }
                }

                LSelect(
                    value = expiry,
                    options = listOf(
                        LSelectOption("30", t("app.settings.tokens.new.expires_in_30_days", "Expires in 30 days")),
                        LSelectOption("90", t("app.settings.tokens.new.expires_in_90_days", "Expires in 90 days")),
                        LSelectOption("365", t("app.settings.tokens.new.expires_in_a_year", "Expires in a year")),
                        LSelectOption("", t("app.settings.tokens.new.never_expires", "Never expires")),
                    ),
                    onValueChange = { expiry = it },
                    label = t("app.settings.tokens.new.expiry", "Expiry"),
                    enabled = !busy,
                )

                // The grant, in English, before the button.
                LCard(Modifier.fillMaxWidth()) {
                    LMuted(summarise(scopes, expiry))
                }

                // Only ever the answer to Create token, and it sits below the button a reader has
                // just pressed — without a live region the press produces silence.
                message?.let {
                    LAlert(
                        t("app.settings.tokens.new.that_didnt_work", "That didn't work"),
                        it,
                        variant = LAlertVariant.WARNING,
                        live = true,
                    )
                }

                Row(Modifier.gap(Space.SM)) {
                    LButton(
                        t("app.settings.tokens.new.create_token", "Create token"),
                        {
                            scope.launch {
                                busy = true
                                message = settingsOperationResult {
                                    ApiClient.createApiToken(
                                        CreateTokenRequest(
                                            name = name.trim(),
                                            scopes = scopes.sortedBy { it.ordinal },
                                            expiresInDays = expiry.toIntOrNull(),
                                        ),
                                    )
                                }.fold({ created = it; null }, { it.message })
                                busy = false
                            }
                        },
                        // Stays enabled even when a scope is above the caller's role: the server
                        // refuses and its message is how somebody learns the rule.
                        enabled = name.isNotBlank() && !busy,
                        loading = busy,
                    )
                    LButton(
                        t("app.settings.tokens.new.cancel", "Cancel"),
                        { ctx.router.tryRoutingTo("/app/settings/tokens") },
                        variant = LButtonVariant.GHOST,
                    )
                }
            }
        }
    }
}

/**
 * The grant as a sentence.
 *
 * "This token can read and write strings, cannot spend credits, and stops working in 90 days" is
 * something somebody reads back to themselves. A row of ticked boxes is not.
 */
private fun summarise(scopes: Set<TokenScope>, expiry: String): String {
    if (scopes.isEmpty()) return "This token could do nothing at all. Tick at least one permission."
    val can = scopes.sortedBy { it.ordinal }.joinToString(", ") { it.id }
    val spends = if (TokenScope.MT_SPEND in scopes) {
        "It can spend machine-translation credits."
    } else {
        "It cannot spend credits."
    }
    val approves = if (TokenScope.STRINGS_APPROVE in scopes) {
        "It can approve translations."
    } else {
        "It cannot approve anything."
    }
    val life = expiry.toIntOrNull()
        ?.let { "It stops working in $it days." }
        ?: "It never expires, so it lives until somebody revokes it."
    return "This token can $can. $spends $approves $life " +
        "It can never touch members, billing, provider keys or other tokens."
}

